import React, { useEffect, useMemo, useRef } from 'react';
import { Column } from 'devextreme-react/data-grid';
import { Item as TabItem } from "devextreme-react/tab-panel";
import Form, { GroupItem, SimpleItem, Label, FormRef } from "devextreme-react/form";
import Guid from "../../models/Guid";
import IEvenement from '../../models/IEvenement';
import { FormListBase, FormListBaseTab, FormListBaseGrid, FormListBaseToolbar } from '../base';
import { Button, ColorBox, FileUploader } from 'devextreme-react';
import useCustomDataSource, { Mode } from "../../hooks/useCustomDataSource";
import FormSysteme from '../../components/forms/form-systeme';
import GestionTypesUnites from '../gestion-types-unites/gestion-types-unites';
import useLookUpBase from '../../components/selectbox/LookupBase';
import GestionSchemas from '../gestion-schemas/gestion-schemas';
import { useCouleurInput, useImageGalleryInput, ImageGalleryPreview } from '../../components/inputs';
import useEnum from '../../hooks/useEnum';

export default function GestionEvenements() {
  const evenementFormDataSource = useCustomDataSource<IEvenement, IEvenement>({ path: "evenement", primaryKey: "idEvenement" });
  const { selectedDataRow: selectedEvenement, mode, setMode, onFieldDataChanged, setSelectedDataRow, setMainForm, isFieldModifier, setIsFieldModifier } = evenementFormDataSource;
  const formRef = useRef<FormRef>(null);

  const { editorOptions: typesUnitesEditorOption, PopupGestion: PopupGestionTypesUnites } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "typeunite",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idTypeUnite", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture
  });

  const { editorOptions: schemasEditorOption, PopupGestion: PopupGestionSchemas } = useLookUpBase({
    isModeModifier: mode !== Mode.Lecture, controllerName: "schemasite",
    onClearRow: () => {
      setIsFieldModifier(true);
      formRef.current?.instance().updateData("idSchemaSite", Guid.empty.toString());
    },
    showClear: mode !== Mode.Lecture, titre: 'Gestionnaire des schémas de site'
  });

  const descriptionOptions = useMemo(() => {
    return { maxLength: 50 };
  }, []);
  const textImageOptions = useMemo(() => {
    return { maxLength: 255 };
  }, []);
  const dateEditorOptions = useMemo(() => {
    return { type: "date" as const, width: '70%' };
  }, []);
  const texteSupplementaireOptions = useMemo(() => {
    return { height: 250 };
  }, []);
  const grandTexteOptions = useMemo(() => {
    return { height: 400 };
  }, []);

  const { couleurOptions: couleur1Options } = useCouleurInput<IEvenement>({ champ: 'couleur1', dataRow: selectedEvenement, setDataRow: setSelectedDataRow, setIsFieldModifier, isModeModifier: mode !== Mode.Lecture, maxWidth: '70%' });
  const { couleurOptions: couleur2Options } = useCouleurInput<IEvenement>({ champ: 'couleur2', dataRow: selectedEvenement, setDataRow: setSelectedDataRow, setIsFieldModifier, isModeModifier: mode !== Mode.Lecture, maxWidth: '70%' });
  const { photoSrc, fileUploaderRef, fileUploaderOptions, removeButtonOptions, noDataText, maxImageHeight, maxImageWidth, showPreviewBackground, previewAlignment } = useImageGalleryInput<IEvenement>({
    champImage: 'image',
    champNomImage: 'nomImage',
    dataRow: selectedEvenement,
    setDataRow: setSelectedDataRow,
    setIsFieldModifier,
    isModeModifier: mode !== Mode.Lecture,
    formRef,
    showPreviewBackground: false,
    previewAlignment: 'left',
  });

  const { editorOptions: etatEditorOptions } = useEnum(undefined, 'gestionEvenementsEtat');
  const prixEditorOptions = useMemo(() => {
    return {
      format: "#,##0.00 $",
      valueChangeEvent: "input",
      elementAttr: { style: "text-align: right;" },
      width: '70%',
    };
  }, []);

  useEffect(() => {
    setMainForm(formRef.current?.instance());
  })

  useEffect(() => {
    if (mode !== Mode.Lecture) formRef.current?.instance().getEditor("description")?.focus();
  }, [mode])

  const setDataRow = (e: string) => {
    setSelectedDataRow((prev: IEvenement) => (
      { ...prev, [e]: Guid.empty.toString() }
    ));
    setIsFieldModifier(true);
  }
  const onSelectionchanged = (item: any, e: string) => {
    setIsFieldModifier(true);
    setSelectedDataRow((prev: IEvenement) => {
      return { ...prev, [e]: item.id }
    })
  }

  return (
    <FormListBase data={evenementFormDataSource}>
      <FormListBaseGrid dataSource={evenementFormDataSource.dataSource}
        onFocusedRowChanged={() => {
          if (mode === Mode.Modifier) {
            setMode(Mode.Lecture);
          }
        }}
      >
        <Column key={"grid-col-description"} dataField={"description"} caption="Description" allowHiding={false} />
        <Column key={"grid-col-artiste"} dataField={"artiste"} caption="Artiste" allowHiding={false} />
        <Column key={"grid-col-dateDebut"} dataField={"dateDebut"} caption="Date début" allowHiding={false} />
        <Column key={"grid-col-dateFin"} dataField={"dateFin"} caption="Date fin" allowHiding={false} />
      </FormListBaseGrid>
      <FormListBaseToolbar headerText={`${selectedEvenement?.description || ''} `} sauvegarderOptions={{ disabled: !isFieldModifier }} annulerOptions={{ disabled: !isFieldModifier }} effacerOptions={{ disabled: !isFieldModifier }} />
      <FormListBaseTab>
        <PopupGestionTypesUnites
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idTypeUnite", data.idTypeUnite);
          }}
        >
          <GestionTypesUnites isModeSelection={true} />
        </PopupGestionTypesUnites>
        <PopupGestionSchemas
          onSelectionchanged={(data: any) => {
            setIsFieldModifier(true);
            formRef.current?.instance().updateData("idSchemaSite", data.idSchemaSite);
          }}
        >
          <GestionSchemas isModeSelection={true} />
        </PopupGestionSchemas>
        <TabItem title="Informations">
          <Form ref={formRef} readOnly={mode === Mode.Lecture} alignItemLabelsInAllGroups={true} formData={selectedEvenement || {} as IEvenement} className={"forms"} labelLocation={"left"} showColonAfterLabel={true} onFieldDataChanged={onFieldDataChanged}>
            <GroupItem colCount={6}>
              <GroupItem colSpan={2}>
                <SimpleItem key={"frm-description"} dataField={"description"}
                  editorOptions={descriptionOptions}
                >
                  <Label text={"Description"} />
                </SimpleItem>
                <SimpleItem key={"frm-invite"} dataField={"artiste"}>
                  <Label text={"Invité"} />
                </SimpleItem>
                <SimpleItem key={"frm-dateDebut"} dataField={"dateDebut"} editorType={"dxDateBox"} editorOptions={dateEditorOptions}>
                  <Label text={"Date début"} />
                </SimpleItem>
                <SimpleItem key={"frm-dateFin"} dataField={"dateFin"} editorType={"dxDateBox"} editorOptions={dateEditorOptions}>
                  <Label text={"Date fin"} />
                </SimpleItem>
                <SimpleItem key={"frm-couleur1"}>
                  <Label text={"Couleur fond"} />
                  <ColorBox {...couleur1Options} />
                </SimpleItem>
                <SimpleItem key={"frm-couleur2"}>
                  <Label text={"Couleur texte"} />
                  <ColorBox {...couleur2Options} />
                </SimpleItem>
                <SimpleItem key={"frm-type"} dataField={"idTypeUnite"}
                  editorType={"dxSelectBox"}
                  editorOptions={typesUnitesEditorOption}
                >
                  <Label text={"Type Unité"} />
                </SimpleItem>
                <SimpleItem key={"frm-schema"} dataField={"idSchemaSite"} editorType={"dxSelectBox"} editorOptions={schemasEditorOption}>
                  <Label text={"Schéma du site"} />
                </SimpleItem>
                <SimpleItem key={"frm-etat"} dataField={"etat"} editorType={"dxSelectBox"} editorOptions={etatEditorOptions}>
                  <Label text={"État"} />
                </SimpleItem>
                <SimpleItem key={"frm-prixAdulte"} dataField={"prixAdulte"}
                  editorType="dxNumberBox"
                  editorOptions={prixEditorOptions}
                >
                  <Label text={"Prix adulte"} />
                </SimpleItem>
                <SimpleItem key={"frm-prixEnfant"} dataField={"prixEnfant"}
                  editorType="dxNumberBox"
                  editorOptions={prixEditorOptions}
                >
                  <Label text={"Prix enfant"} />
                </SimpleItem>
                <SimpleItem key={"frm-adresse"} dataField={"adresseEvenement"}>
                  <Label text={"Adresse évènement"} />
                  {/* <TextBox /> */}
                </SimpleItem>
                <SimpleItem key={"frm-texte"} dataField={"texteSupplementaire"} editorType={"dxTextArea"} editorOptions={texteSupplementaireOptions}>
                  <Label text={"Texte supplémentaire"} />
                </SimpleItem>
                <SimpleItem key={"frm-actif"} dataField={"actif"} editorType={"dxCheckBox"} />
                <GroupItem caption="Image">
                  <SimpleItem key={"frm-imageButtons"}>
                    <Label visible={false} />
                    <div className="galerie-image-barre-outils">
                      <FileUploader ref={fileUploaderRef} {...fileUploaderOptions} />
                      <Button {...removeButtonOptions} />
                    </div>
                  </SimpleItem>
                  <SimpleItem key={"text-donneImage"}>
                    <Label visible={false} />
                    <ImageGalleryPreview
                      photoSrc={photoSrc}
                      noDataText={noDataText}
                      maxHeight={maxImageHeight}
                      maxWidth={maxImageWidth}
                      showBackground={showPreviewBackground}
                      previewAlignment={previewAlignment}
                    />
                  </SimpleItem>
                </GroupItem>

              </GroupItem>
              <GroupItem colSpan={4}>
                <GroupItem caption={"Notes"}>
                  <SimpleItem key={"text-area-notes"} dataField={"notes"} editorType={"dxTextArea"} editorOptions={grandTexteOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
                <GroupItem caption={"Description web"}>
                  <SimpleItem key={"text-descriptionWeb"} dataField={"descriptionWeb"} editorType={"dxTextArea"} editorOptions={grandTexteOptions}>
                    <Label visible={false} />
                  </SimpleItem>
                </GroupItem>
              </GroupItem>
            </GroupItem>
          </Form>
        </TabItem>
        <TabItem title="Système"><FormSysteme formData={selectedEvenement || {} as IEvenement} /></TabItem>
      </FormListBaseTab>
    </FormListBase>
  );
}
